// Shared server-backed store with record-level concurrency protection.
import {
  syncedGet,
  syncedSet,
  createSampleRecord,
  updateSampleRecord,
  updateSampleIdentityRecord,
  deleteSampleRecord,
  createInvoiceRecord,
  updateInvoiceRecord,
  deleteInvoiceRecord,
  reserveCounterValues,
} from './serverStore';
const KEYS = { samples: 'sl_samples', invoices: 'sl_invoices', counters: 'sl_counters', pricing: 'sl_pricing' };
const LOGIN_QUEUE_KEY = 'sl_sample_login_queue';

const defaultCounters = { nextSampleNum: 86725, nextInvoiceNum: 1926, nextWorkOrder: 130,
  samplePrefix: '', invoicePrefix: '26-', workOrderPrefix: '26-0',
  samplePad: 0, invoicePad: 4, workOrderPad: 3 };

function load(key, fallback) { return syncedGet(key, fallback); }
function save(key, val) { syncedSet(key, val); }

// ── Seed demo data on first load ──────────────────────────────────────────────
// Demo data seeding removed — server is single source of truth

// ── Always sync counters to max existing IDs on load ─────────────────────────
// This ensures new samples always get IDs higher than any existing sample,
// regardless of how samples were added (import, duplicate, receive, etc.)
function syncCounters() {
  const samples = load(KEYS.samples, []);
  // If no samples, don't overwrite configured starting counters
  if (samples.length === 0) return;
  const counters = load(KEYS.counters, defaultCounters);
  
  const maxSampleId = Math.max(...samples.map(s => Number(s.id) || 0));
  const maxWO = Math.max(...samples.map(s => {
    const m = s.workOrder?.match(/-(\d+)$/);
    return m ? parseInt(m[1]) : 0;
  }));
  const invoices = load(KEYS.invoices, []);
  const maxInvNum = Math.max(...invoices.map(i => parseInt(i.num) || 0));

  const updated = {
    ...counters,  // preserve prefix/pad settings
    nextSampleNum:  Math.max(counters.nextSampleNum,  maxSampleId + 1),
    nextInvoiceNum: Math.max(counters.nextInvoiceNum, maxInvNum + 1),
    nextWorkOrder:  Math.max(counters.nextWorkOrder,  maxWO + 1),
  };
  save(KEYS.counters, updated);
}
// ── Public API ────────────────────────────────────────────────────────────────
export const db = {
  getSamples: () => {
    const arr = load(KEYS.samples, []);
    // Deduplicate by ID — keep first occurrence (most recently added, since unshift)
    const seen = new Set();
    return arr.filter(s => {
      const key = String(s.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  },
  saveSamples: () => Promise.reject(new Error('Use record-level sample operations')),

  getInvoices: () => load(KEYS.invoices, []),
  saveInvoices: () => Promise.reject(new Error('Use record-level invoice operations')),

  getCounters: () => load(KEYS.counters, defaultCounters),
  saveCounters: (c) => save(KEYS.counters, c),

  async reserveSampleNums(count = 1) {
    return reserveCounterValues('sample', count);
  },
  async nextSampleNum() {
    const [value] = await this.reserveSampleNums(1);
    return value;
  },
  async nextInvoiceNum() {
    const [value] = await reserveCounterValues('invoice', 1);
    return value;
  },
  async nextWorkOrder() {
    const [value] = await reserveCounterValues('workOrder', 1);
    return value;
  },

  addSample(s) {
    const arr = this.getSamples();
    // Guard against duplicates
    if (arr.some(existing => String(existing.id) === String(s.id))) {
      console.warn('Duplicate sample ID prevented:', s.id);
      return Promise.reject(new Error(`Sample ${s.id} already exists`));
    }
    // The edit-protection window must be based on an immutable server-synced
    // timestamp, not on sample-number order or a date that users can edit.
    return createSampleRecord({
      ...s,
      createdAt: new Date().toISOString(),
    });
  },
  updateSample(id, updates) {
    const arr = this.getSamples();
    const current = arr.find(s => String(s.id) === String(id));
    if (!current) return Promise.reject(new Error(`Sample ${id} not found`));
    return updateSampleRecord(current.id, updates);
  },
  updateSampleIdentity(id, newId, workOrder) {
    return updateSampleIdentityRecord(id, newId, workOrder);
  },
  deleteSample(id) {
    return deleteSampleRecord(id);
  },

  addInvoice(inv) {
    return createInvoiceRecord(inv);
  },
  updateInvoice(num, updates) {
    const arr = this.getInvoices();
    const current = arr.find(i => i.num === num);
    if (!current) return Promise.reject(new Error(`Invoice ${num} not found`));
    return updateInvoiceRecord(current._recordId || current.id || current.num, updates);
  },
  deleteInvoice(num) {
    const current = this.getInvoices().find(i => i.num === num);
    if (!current) return Promise.reject(new Error(`Invoice ${num} not found`));
    return deleteInvoiceRecord(current._recordId || current.id || current.num);
  },

  // ── Incoming samples awaiting full LIMS login ───────────────────────────
  getLoginQueue: () => load(LOGIN_QUEUE_KEY, []),

  async addLoginQueueItem(item) {
    const queue = load(LOGIN_QUEUE_KEY, []);
    const record = {
      id: item.id || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      company: String(item.company || '').trim(),
      receivedDate: String(item.receivedDate || '').trim(),
      sampleCount: Math.max(1, Number(item.sampleCount) || 1),
      addedAt: item.addedAt || new Date().toISOString(),
      addedBy: String(item.addedBy || '').trim(),
    };
    await save(LOGIN_QUEUE_KEY, [record, ...queue]);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lims-login-queue-changed'));
    }
    return record;
  },

  async removeLoginQueueItem(id) {
    const identity = String(id);
    const queue = load(LOGIN_QUEUE_KEY, []);
    await save(LOGIN_QUEUE_KEY, queue.filter(item => String(item.id) !== identity));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lims-login-queue-changed'));
    }
    return true;
  },

  // ── Pricing ───────────────────────────────────────────────────────────────
  getDefaultPricing() {
    return {
      // Test category base prices
      byCategory: {
        'Specific Analysis': 250,
        'NPDES': 320,
        'Aquatic Toxicity': 400,
        'Wells': 360,
        'TCLP': 500,
        'Fuel': 385,
        'QA/QC': 180,
        'Coal': 280,
        'Stormwater': 300,
        'Industrial Wastewater': 340,
        'Drinking Water': 290,
        'Soil/Sediment': 320,
      },
      // Per-parameter prices (override category price)
      byParam: {
        'Al-Aluminum Total': 25,
        'Cu-Copper Total': 25,
        'Pb-Lead Total': 30,
        'Hg-Mercury Total': 45,
        'As-Arsenic Total': 30,
        'Cd-Cadmium Total': 30,
        'Cr-Chromium Total': 25,
        'pH': 15,
        'Total Suspended Solids': 20,
        'Total Dissolved Solids': 20,
        'BOD5': 35,
        'Chemical Oxygen Demand': 30,
        'Total Organic Carbon': 40,
        'Fecal Coliform': 35,
        'Turbidity': 15,
        'Conductivity': 15,
        'Hardness as CaCO3': 20,
        'Ammonia-N': 25,
        'Nitrogen-TKN': 30,
        'Total Phosphorous': 25,
        'Sulfate, as SO4': 20,
        'Chloride': 20,
        'Fe-Iron Total': 25,
        'Mn-Manganese Total': 25,
        'Zn-Zinc Total': 25,
        'Ni-Nickel Total': 25,
        'API Gravity @ 60°F': 30,
        'Flash Point, °F': 35,
        'Cetane Index': 40,
        'Sulfur, %': 35,
        'Viscosity, SSF @ 100°F': 30,
        'Pour Point, °F': 30,
        'Cloud Point, °F': 30,
        'BTU/Lb': 25,
        'Ash,%': 20,
        'Moisture, %': 20,
        'Fixed Carbon, %': 20,
        'Volatile Matter, %': 20,
      },
      // Rush multipliers
      rushMultiplier: { 1: 2.0, 2: 1.75, 3: 1.5, 4: 1.25, 5: 1.0 },
      // Minimum invoice amount
      minimumCharge: 50,
    };
  },

  getPricing() {
    const stored = load(KEYS.pricing, null);
    return stored || this.getDefaultPricing();
  },
  savePricing(p) { save(KEYS.pricing, p); },

  // Calculate price for a sample based on its params and category
  calcSamplePrice(sample, pricingOverride) {
    if (!sample) return 0;
    const pricing = pricingOverride || this.getPricing();
    const params = sample.params || [];
    if (params.length === 0) {
      // Fall back to category price
      return pricing.byCategory[sample.testCategory] || 250;
    }
    // Sum per-param prices, fall back to category / default
    const categoryBase = pricing.byCategory[sample.testCategory] || 250;
    const paramTotal = params.reduce((sum, p) => {
      return sum + (pricing.byParam[p] || 0);
    }, 0);
    const subtotal = paramTotal > 0 ? paramTotal : categoryBase;
    // Apply rush multiplier
    const rush = sample.rush ? Math.min(sample.rush, 5) : 5;
    const mult = pricing.rushMultiplier[rush] || 1.0;
    return Math.max(Math.round(subtotal * mult * 100) / 100, pricing.minimumCharge);
  },

};

// ── Approval Request System ───────────────────────────────────────────────────
const APPROVAL_KEY = 'sl_approvals';

export const approvals = {
  getAll: () => load(APPROVAL_KEY, []),
  save: (arr) => save(APPROVAL_KEY, arr),

  // Technicians may edit a sample during its first 24 hours. After that,
  // shared approval gates require an administrator. New samples carry an
  // immutable createdAt timestamp; older records fall back to received date
  // plus received time so installations can adopt this rule immediately.
  EDIT_WINDOW_HOURS: 24,

  getEditReferenceTime(sample) {
    if (!sample) return null;

    const createdAt = Date.parse(String(sample.createdAt || ''));
    if (Number.isFinite(createdAt)) return createdAt;

    const dateText = String(sample.received || '').trim();
    if (!dateText) return null;
    const isoDate = dateText.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!isoDate) {
      const parsed = Date.parse(dateText);
      return Number.isFinite(parsed) ? parsed : null;
    }

    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    const timeText = String(sample.timeReceived || '').trim();
    if (timeText) {
      const timeMatch = timeText.match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i
      );
      if (timeMatch) {
        hours = Number(timeMatch[1]);
        minutes = Number(timeMatch[2]);
        seconds = Number(timeMatch[3] || 0);
        const meridiem = String(timeMatch[4] || '').toUpperCase();
        if (meridiem === 'AM' && hours === 12) hours = 0;
        if (meridiem === 'PM' && hours < 12) hours += 12;
      }
    }

    const localDate = new Date(
      Number(isoDate[1]),
      Number(isoDate[2]) - 1,
      Number(isoDate[3]),
      hours,
      minutes,
      seconds,
      0
    );
    const timestamp = localDate.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  },

  canEditFreely(sampleId, now = Date.now()) {
    const samples = load(KEYS.samples, []);
    const sample = samples.find(s => String(s.id) === String(sampleId));
    const referenceTime = this.getEditReferenceTime(sample);
    if (!Number.isFinite(referenceTime)) return false;
    const ageMs = Number(now) - referenceTime;
    return ageMs <= this.EDIT_WINDOW_HOURS * 60 * 60 * 1000;
  },

  // Submit a change request for admin approval
  request({ sampleId, requestedBy, requestedByName, changeType, description, proposedChanges, currentValues }) {
    const all = this.getAll();
    const req = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2,5),
      sampleId,
      requestedBy,
      requestedByName,
      changeType,       // 'edit_sample' | 'delete_sample' | 'edit_results' | 'mark_complete' | 'edit_invoice'
      description,
      proposedChanges,  // what they want to change
      currentValues,    // snapshot of current values
      status: 'pending', // 'pending' | 'approved' | 'rejected'
      submittedAt: new Date().toISOString(),
      reviewedAt: null,
      reviewedBy: null,
      reviewNote: '',
    };
    all.unshift(req);
    save(APPROVAL_KEY, all);
    return req;
  },

  approve(id, reviewedBy, note = '') {
    const all = this.getAll();
    const idx = all.findIndex(r => r.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], status:'approved', reviewedAt:new Date().toISOString(), reviewedBy, reviewNote:note };
    save(APPROVAL_KEY, all);
    return all[idx];
  },

  reject(id, reviewedBy, note = '') {
    const all = this.getAll();
    const idx = all.findIndex(r => r.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], status:'rejected', reviewedAt:new Date().toISOString(), reviewedBy, reviewNote:note };
    save(APPROVAL_KEY, all);
    return all[idx];
  },

  getPending: () => load(APPROVAL_KEY, []).filter(r => r.status === 'pending'),
  getCount:   () => load(APPROVAL_KEY, []).filter(r => r.status === 'pending').length,
};
